// ============================================================
// combatantSyncRead - apply one view of the combatants collection.
//
// The listener's half of useCombatantSync, and the roster audit's. One
// function applies a view whether the listener delivered it or the
// audit read it (v8.362.1). Not React: the hook builds the context in
// its listener effect, so every value here is the one that effect
// subscribed with - exactly as the closure it replaced.
//
// ECHO DECISIONS ARE MADE HERE, ONCE, OUTSIDE THE UPDATER.
// `echo.shouldAccept` MUTATES its cache, and React invokes a state
// updater more than once (StrictMode on every update). Run inside the
// updater, the second invocation saw the first one's cache write,
// concluded "no change" and kept the OLD combatant - the post-undo sync
// latch. The updater itself is `mergeRead`, which is pure.
// ============================================================

import type { MutableRefObject } from 'react';
import type { CollectionRead } from '../backend/types';
import type { Combatant } from '../engine/roster/types';
import { stateContent } from '../engine/roster/rev';
import { parsedCombatants } from '../engine/roster/serialize';
import type { ParsedCombatant } from '../engine/roster/serialize';
import type { EchoPrevention } from './useEchoPrevention';
import { isWriteTraceOn, noteRosterWrite } from '../diagnostics/writeTrace';
import { departedIn, mergeRead } from './combatantRosterMerge';
import type { ReadDecision, ReadPlan } from './combatantRosterMerge';
import { diag, errorSummary } from '../diagnostics/log';
import { toMs } from '../utils/timestamps';

type Timers = Record<string, ReturnType<typeof setTimeout>>;
/**
 * One view of the collection. `listed`: a list fetched on request (the roster audit), which can be older than what
 * the stream has brought since it was served - it may bring only what is strictly newer (`decideOne`). `asked`: the
 * moment the list was asked for - the stream's count then, and the ids confirmed then.
 */
type Read = CollectionRead<Record<string, unknown>> & { listed?: boolean; asked?: Asked };

/** When an audit asked: the stream's view count, and the ids confirmed, at that moment. */
interface Asked {
  seq: number;
  confirmed: Set<string>;
}

/** The two echo keys per combatant - one per half, or the halves invalidate each other. */
export const stateKey = (id: string) => `state:${id}`;
export const sheetKey = (id: string) => `sheet:${id}`;

/** The refs the listener shares with the writers, each documented here once (useCombatantSync makes them). */
interface CombatantSyncRefs {
  sheetTimers: MutableRefObject<Timers>;
  initialSyncDone: MutableRefObject<boolean>;
  /**
   * Combatant ids this client has actually seen in a snapshot. The remote set is NOT the complete roster: a combatant
   * added a moment ago has not been written yet. A local is only removed once its id has been confirmed remotely and
   * then disappears - a real deletion.
   */
  confirmedIds: MutableRefObject<Set<string>>;
  /** Ids a view has shown this screen WITH their sheet: a sheetless copy is a stub - see `usableEntries`. */
  sheetSeenRef: MutableRefObject<Set<string>>;
  /** The roster as of this render - the listener closure would otherwise be stale. */
  rosterRef: MutableRefObject<Combatant[]>;
  /** Has this client adopted the SHARED roster yet? See useCombatantSync's `hydrated`. */
  hydratedRef: MutableRefObject<boolean>;
  /**
   * Ids this client has just deleted, until the deletion comes back. A DELETE IS NOT INSTANT AND A SNAPSHOT IS NOT
   * ORDERED AROUND IT: any snapshot arriving before it lands re-adds the combatant just removed - a recalled marker
   * reappeared on the map (v8.30.0). Cleared when a snapshot stops listing the id, or when the delete is answered.
   */
  removedRef: MutableRefObject<Set<string>>;
  /**
   * Ids a view of the server took off this roster, until the departure effect asks about them: a deletion HEARD,
   * whose delete is somebody else's and is NOT sent again - see `noteHeard`.
   */
  heardGoneRef: MutableRefObject<Set<string>>;
  /** The disagreement each half last re-sent for, by echo key - see `settleResend`. */
  resentRef: MutableRefObject<Map<string, string>>;
  /**
   * The stream's views, counted, and the view each id was last heard GONE in. An audit's list was served when it was
   * ASKED for: it may not adopt an id removed since - see `noteStream`.
   */
  streamSeq: MutableRefObject<number>;
  goneAt: MutableRefObject<Map<string, number>>;
}

/** Everything one subscription reads and writes through. */
export interface ReadContext extends CombatantSyncRefs {
  /** When false the listener writes and migrates but does not drive local state. */
  authoritative: boolean | undefined;
  /** The view being decided is a requested list (`Read.listed`): it brings only strictly newer halves. */
  listing?: boolean;
  /** The view being decided has none of this screen's writes in flight (`Read.listsEveryone`): the server's word. */
  listsEveryone?: boolean;
  isGm: boolean;
  echo: EchoPrevention;
  setRoster: (updater: (prev: Combatant[]) => Combatant[]) => void;
  setHydrated: (hydrated: boolean) => void;
  /**
   * Send this screen's state again (v8.393.9): the server holds an OLDER
   * revision than ours, so a stale write from another screen replaced it
   * - the server keeps the last write, not the newest. Absent = no re-send.
   */
  resendState?: (local: Combatant) => void;
  /** The same for the cold half (v8.398.9): the server's sheet is older than ours. */
  resendSheet?: (local: Combatant) => void;
}

function markHydrated(ctx: ReadContext): void {
  ctx.hydratedRef.current = true;
  ctx.setHydrated(true);
}

/** Cache both halves of a remote combatant as already seen. */
function cacheRemote(echo: EchoPrevention, { combatant: r, rawCold }: ParsedCombatant): void {
  echo.setCache(stateKey(r.id), stateContent(r.state));
  echo.setCache(sheetKey(r.id), rawCold);
}

/**
 * The first shared roster this client has seen: adopt it wholesale -
 * merging would keep the local placeholder alongside it.
 *
 * ADOPT ONLY WHAT CARRIES A SHEET. A client that joined while somebody was
 * adding a combatant adopted the FABRICATED Character `combatantFromDoc`
 * makes for a document whose sheet has not landed, and its own sheet sync
 * wrote that back over the real one (measured live: the GM's character
 * became "New Character", authored by the PLAYER). Skipping loses nothing:
 * the cold write is right behind, and the merge path introduces it.
 */
function adoptWholesale(ctx: ReadContext, parsed: ParsedCombatant[]): void {
  markHydrated(ctx);
  const real = parsed.filter((p) => p.hasSheet);
  for (const p of real) {
    cacheRemote(ctx.echo, p);
    ctx.confirmedIds.current.add(p.combatant.id);
    ctx.sheetSeenRef.current.add(p.combatant.id);
  }
  ctx.setRoster(() => real.map((p) => p.combatant));
}

/**
 * WHAT WE HAVE JUST DELETED IS NOT NEWS, until the view stops listing it -
 * which is exactly when the delete has landed. See the hook's `removedRef`.
 */
function forgetLandedDeletes(removed: Set<string>, parsed: ParsedCombatant[]): void {
  const stillListed = new Set(parsed.map((p) => p.combatant.id));
  for (const id of removed) if (!stillListed.has(id)) removed.delete(id);
}

/**
 * The entries this view may use. A document with no `sheet` is a
 * state-only write that landed ahead of its cold half (350ms vs 700ms):
 * it may UPDATE a combatant we know, never introduce one - introducing
 * one materialises the fabricated Character on the loader's own screen.
 *
 * AND ONLY ONE NEVER SEEN WHOLE (2026-10-17). A document loses its sheet
 * only by being deleted and re-created by a state write: the GM struck
 * someone, and a player who had not heard yet saved their state, merging a
 * stub into being. Taken as an update, the players kept the person for good
 * while the GM refused them. Now the stub is not usable, so a complete view
 * prunes the person here too - as heard, sending no delete of its own.
 */
function usableEntries(ctx: ReadContext, parsed: ParsedCombatant[]): ParsedCombatant[] {
  const seen = ctx.sheetSeenRef.current;
  const known = (id: string) => ctx.rosterRef.current.some((c) => c.id === id);
  const fresh = (id: string) => known(id) && !seen.has(id);
  const usable = parsed.filter(
    (p) => !ctx.removedRef.current.has(p.combatant.id) && (p.hasSheet || fresh(p.combatant.id)),
  );
  for (const p of usable) if (p.hasSheet) seen.add(p.combatant.id);
  return usable;
}

/** The remote cold half, in the shape the accept side compares against the sheet echo key. */
function remoteCold(r: Combatant): Record<string, unknown> {
  return {
    name: r.name,
    kind: r.kind,
    order: r.order,
    sheet: JSON.stringify(r.sheet),
    overrides: JSON.stringify(r.overrides),
    loadoutRef: r.loadoutRef,
    slotOrder: r.slotOrder ?? null,
    schemaVersion: 2,
  };
}

/**
 * Decide one remote combatant against the local one. Each half is
 * accepted independently - a remote wound tick must not roll back a
 * sheet edit made a moment ago, so a pending sheet write keeps the local
 * sheet. The clock is a VETO on stale echoes (a half written strictly
 * BEFORE what we hold), not a replacement for the content comparison;
 * the cold half has had the same veto since v8.273.0 (the kit-arming
 * race). A document with no sheet carries a FABRICATED one and may never
 * overwrite a real sheet. The echo calls short-circuit in this order.
 */
function decideOne(ctx: ReadContext, p: ParsedCombatant, local: Combatant | undefined): ReadDecision {
  const { combatant: r, hasSheet } = p;
  if (!local) {
    cacheRemote(ctx.echo, p);
    return { r, takeState: true, takeSheet: true, hasSheet };
  }
  const newer = (r.state.rev ?? 0) >= (local.state.rev ?? 0);
  // A LIST AT THE SAME REVISION IS NOT NEWS (2026-10-17): two seats changing one combatant from the same base both
  // write the same next rev; the stream settles every screen on the server's last copy, and a list served before it
  // put the other copy back on one screen. Every real change bumps the rev, so a list need only bring a higher one.
  const ahead = ctx.listing ? (r.state.rev ?? 0) > (local.state.rev ?? 0) : newer;
  const takeState = ahead && ctx.echo.shouldAccept(stateKey(r.id), stateContent(r.state));
  // Refusing an older copy means the server lost ours: put it back, or
  // this screen and every other stay apart for good (./dev fuzz).
  const behind = newer ? null : `${local.state.rev ?? 0}>${r.state.rev ?? 0}`;
  settleResend(ctx, stateKey(r.id), behind, () => ctx.resendState?.(local));
  traceReceive(r.id, 'recv-state', takeState, newer, `${r.state.rev ?? 0} vs ${local.state.rev ?? 0}`);
  return { r, takeState, takeSheet: decideSheet(ctx, p, local), hasSheet };
}

/**
 * The cold half: taken when it is real, no sheet write of ours is pending,
 * it is not older than ours, and it is new content. An OLDER sheet with
 * nothing of ours in flight means the server lost our write - a stale
 * write replaced it - so ours goes back up (v8.398.9), as the state's does.
 */
function decideSheet(ctx: ReadContext, { combatant: r, hasSheet }: ParsedCombatant, local: Combatant): boolean {
  if (!hasSheet || ctx.sheetTimers.current[r.id] !== undefined) return false;
  const [mine, theirs] = [local.sheetRev ?? 0, r.sheetRev ?? 0];
  settleResend(ctx, sheetKey(r.id), theirs < mine ? `${mine}>${theirs}` : null, () => ctx.resendSheet?.(local));
  const ahead = ctx.listing ? theirs > mine : theirs >= mine; // a list brings only what is newer, as the state
  return ahead && ctx.echo.shouldAccept(sheetKey(r.id), remoteCold(r));
}

/**
 * RE-SEND ONCE PER DISAGREEMENT (2026-10-01). `behind` names it - our clock
 * against the server's older one - or is null when the server agrees. The
 * same disagreement seen again is not re-sent: the re-send is in flight, or
 * it was refused and sending it on every view only repeats the refusal (a
 * screen at the table sent PATCH 404 / POST 400 for minutes, once per view).
 * Agreement forgets it, so the next stale write is answered again.
 *
 * ONLY THE SERVER'S AGREEMENT (2026-11-01). A view with our write in flight
 * shows our own copy back, and that agrees with us by definition. Forgetting
 * there, a refused re-send was made again on every round trip: the GM hid a
 * figure a player held, the player's re-sent sheet failed on the deleted
 * document, and Firestore alternated the two views every ~60 ms for good -
 * the write never stopped, and the dead figure stayed on the player's screen.
 */
function settleResend(ctx: ReadContext, key: string, behind: string | null, resend: () => void): void {
  const sent = ctx.resentRef.current;
  if (behind === null) {
    if (ctx.listsEveryone !== false) sent.delete(key);
  } else if (sent.get(key) !== behind) {
    sent.set(key, behind);
    resend();
  }
}

/**
 * What became of an incoming half, for the sandbox's write trace: taken,
 * vetoed as older, or dropped as content already seen. Nothing elsewhere.
 */
function traceReceive(id: string, kind: string, took: boolean, newer: boolean, clocks: string): void {
  if (!isWriteTraceOn()) return;
  noteRosterWrite(id, kind, took ? `took ${clocks}` : newer ? `same content ${clocks}` : `veto ${clocks}`);
}

/** Every decision, against the roster held right now, in view order. */
function decideAll(ctx: ReadContext, usable: ParsedCombatant[]): Map<string, ReadDecision> {
  const byIdNow = new Map(ctx.rosterRef.current.map((c) => [c.id, c]));
  const decisions = new Map<string, ReadDecision>();
  for (const p of usable) decisions.set(p.combatant.id, decideOne(ctx, p, byIdNow.get(p.combatant.id)));
  return decisions;
}

/**
 * Merge a view into a hydrated roster. `listsEveryone` and `removed` say
 * whether an absence means a deletion: mid-burst a view does not list
 * everyone, and pruning on it deleted re-deployed combatants for good
 * (v8.22.0).
 */
function mergeView(ctx: ReadContext, parsed: ParsedCombatant[], read: Read): void {
  forgetLandedDeletes(ctx.removedRef.current, parsed);
  const usable = usableEntries(ctx, parsed);
  const remoteIds = new Set(usable.map((p) => p.combatant.id));
  const removedIds = new Set(read.removed);
  const previouslyConfirmed = confirmedFor(ctx, read);
  for (const id of remoteIds) ctx.confirmedIds.current.add(id);
  const decisions = decideAll({ ...ctx, listing: !!read.listed, listsEveryone: read.listsEveryone }, usable);
  const plan = { decisions, remoteIds, removedIds, listsEveryone: read.listsEveryone, previouslyConfirmed };
  noteHeard(ctx, plan);
  ctx.setRoster((prev) => mergeRead(prev, plan));
}

/**
 * A DEPARTURE HEARD IS NOT A DEPARTURE MADE (2026-10-06). Every screen's
 * departure effect calls `removeCombatant` for whoever left its roster, and
 * for a deletion somebody else made that was the same delete sent again,
 * late: after a scene cut struck a traveller and staged them again, a
 * player's echo landed on the RE-CREATED document and took it (and its
 * token) off the server. The ids this view prunes are noted here;
 * `removeCombatant` sends nothing for them.
 *
 * A VIEW THAT LISTS THE ID AGAIN DOES NOT FORGET IT: that view can arrive
 * before the departure effect of the commit that dropped them has run, and
 * forgetting there sent the very echo this is for (the review, same day).
 * A note nobody asks about - the two views folded into one render, so
 * nobody ever left the roster - is dropped by the departure pass itself,
 * which says who is on the roster (`forgetHeard`).
 */
function noteHeard(ctx: ReadContext, plan: ReadPlan): void {
  for (const id of departedIn(ctx.rosterRef.current, plan)) ctx.heardGoneRef.current.add(id);
}

/**
 * THE AUDIT IS OLDER THAN THE STREAM (Mission 6, 2026-10-21). An audit's list was served when it was ASKED for,
 * and the stream kept talking while it was on its way. Two things it must not overrule:
 * - someone ADDED since: the list prunes only ids confirmed when it was asked for (`asked.confirmed`), so an id
 *   confirmed later is not pruned by its absence;
 * - someone REMOVED since: the stream's views are counted and each removal noted with its view (a removed id, or a
 *   confirmed id a complete view no longer lists), and the list may not adopt an id removed after the ask.
 * Only removals are noted, never presence: a PocketBase view is the whole cache every time, so "heard since" would
 * cover every id and the audit would heal nothing.
 */
function noteStream(ctx: ReadContext, read: Read): void {
  const seq = ++ctx.streamSeq.current;
  const gone = ctx.goneAt.current;
  for (const id of read.removed) gone.set(id, seq);
  if (!read.listsEveryone) return;
  const listed = new Set(read.entries.map((e) => e.id));
  // The confirmed ids, not the rendered roster: a view adopted a moment ago may not have rendered yet.
  for (const id of ctx.confirmedIds.current) if (!listed.has(id)) gone.set(id, seq);
}

/** Has the stream said this id is gone since the audit asked? */
const goneSince = (ctx: ReadContext, asked: Asked, id: string) => (ctx.goneAt.current.get(id) ?? 0) > asked.seq;

/** The ids whose absence from this view is a deletion: for an audit's list, those confirmed when it was asked. */
function confirmedFor(ctx: ReadContext, read: Read): Set<string> {
  return new Set(read.asked ? read.asked.confirmed : ctx.confirmedIds.current);
}

/** Notes older than the audit that just settled can never matter again: one audit runs at a time. */
function forgetNotesBefore(ctx: ReadContext, seq: number): void {
  for (const [id, at] of ctx.goneAt.current) if (at <= seq) ctx.goneAt.current.delete(id);
}

/**
 * Apply one view of the collection.
 *
 * Nobody writes until their first view has been seen, and hydration is
 * decided on what the collection CONTAINS, never on who you are: an empty
 * collection is seeded by the GM (everyone else keeps waiting rather than
 * racing to be first); the first non-empty view is adopted wholesale;
 * every later one is merged.
 */
export function applyRead(ctx: ReadContext, read: Read): void {
  if (!ctx.authoritative) return; // write-only during the transition
  if (!read.listed) noteStream(ctx, read);
  const parsed = parsedCombatants(read.entries);
  ctx.initialSyncDone.current = true;
  if (ctx.hydratedRef.current) return mergeView(ctx, parsed, read);
  // Not hydrated yet: an empty collection is a new session the GM seeds.
  // ONLY then - once hydrated, an empty view is a table everybody left, and
  // its removals must land: a scene cut that parks everyone empties the
  // collection, and returning here kept the parked on every player's
  // screen for good (overnight fuzz, v8.400.3).
  if (parsed.length) adoptWholesale(ctx, parsed);
  else if (ctx.isGm) markHydrated(ctx);
}

// ── The roster audit (v8.362.1) ──────────────────────────────

/** What the audit's read of the server says about this client's roster. */
function rosterDrift(ctx: ReadContext, parsed: ParsedCombatant[], confirmed: Set<string>) {
  const server = new Set(parsed.filter((p) => p.hasSheet).map((p) => p.combatant.id));
  const local = new Set(ctx.rosterRef.current.map((c) => c.id));
  const missing = [...server].filter((id) => !local.has(id) && !ctx.removedRef.current.has(id));
  const extra = [...local].filter((id) => !server.has(id) && confirmed.has(id));
  return { missing, extra, serverSize: server.size };
}

/** The difference is LOGGED before it is healed, so a stale client stops being silent. */
function reportDrift(why: string, { missing, extra, serverSize }: ReturnType<typeof rosterDrift>): void {
  if (missing.length || extra.length) {
    const summary = `${missing.length} missing here, ${extra.length} gone from the server - reconciling`;
    diag.warn('sync', `roster audit (${why}): ${summary}`, { missing, extra });
  } else if (why !== 'tick') {
    diag.info('sync', `roster audit (${why}): in step, ${serverSize} on the server`);
  }
}

/**
 * Report what the server's list says about the drift, then apply it as a COMPLETE view - less every id the stream
 * has said is gone since the list was asked for (`noteStream`): the list is older than that.
 */
function healFrom(ctx: ReadContext, docs: { id: string; data: Record<string, unknown> }[], why: string, asked: Asked) {
  const read = { entries: docs, docs: docs.map((d) => d.data), listsEveryone: true, removed: [], listed: true, asked };
  reportDrift(why, rosterDrift(ctx, parsedCombatants(docs), confirmedFor(ctx, read)));
  applyRead(ctx, read);
}

/** The audit's one read of the server. Optional: a source without it gets no audit. */
interface AuditSource {
  listCombatants?: (sessionId: string) => Promise<{ id: string; data: Record<string, unknown> }[]>;
  deleteCombatant?: (sessionId: string, id: string) => Promise<void>;
}

/**
 * How old a sheetless document must be before the GM's audit deletes it, ms. A fresh add writes its sheet 350 ms
 * after its state; two minutes is far past that, and past any clock skew between the screens that stamp `updatedAt`.
 */
export const STUB_AGE_MS = 120000;

/**
 * STUBS LEFT ON THE SERVER (2026-10-17): a sheetless document nobody holds - a struck person a player's half-write
 * re-created before half-writes stopped creating, or an add whose sheet never came. Every screen ignores it
 * (`usableEntries`), so nothing would ever take it away. The GM's audit deletes one old enough not to be an add in
 * flight; a player never deletes.
 */
function sweepStubs(
  ctx: ReadContext,
  mp: AuditSource,
  sessionId: string,
  docs: { id: string; data: Record<string, unknown> }[],
) {
  if (!ctx.isGm || !mp.deleteCombatant) return;
  const held = new Set(ctx.rosterRef.current.map((c) => c.id));
  const old = (d: { data: Record<string, unknown> }) =>
    Date.now() - toMs(d.data.updatedAt as Parameters<typeof toMs>[0]) > STUB_AGE_MS;
  const stubs = parsedCombatants(docs).filter((p) => !p.hasSheet && !held.has(p.combatant.id));
  for (const id of stubs.map((p) => p.combatant.id).filter((id) => old(docs.find((d) => d.id === id)!))) {
    diag.info('sync', `roster audit: deleting a stub document nobody holds (${id})`);
    mp.deleteCombatant(sessionId, id).catch((e) => diag.warn('sync', `stub delete failed (${id})`, errorSummary(e)));
  }
}

/**
 * THE ROSTER AUDIT. Every sync bug this project could not reproduce ended
 * the same way: a client holding a roster the server did not, until a
 * reload. The returned function reads the collection itself and applies
 * it through the same rules the listener obeys - an unwritten local is
 * kept, a document never seen is adopted. One audit at a time; only once
 * hydrated and authoritative; a failure keeps the view; `stop()` on teardown.
 */
export function rosterAuditor(ctx: ReadContext, mp: AuditSource, sessionId: string) {
  let auditing = false;
  let stopped = false;
  const audit = async (why: string): Promise<void> => {
    if (!mp.listCombatants || !ctx.hydratedRef.current || !ctx.authoritative || auditing) return;
    auditing = true;
    // The list is served as of NOW: what the stream says after this outranks it.
    const asked = { seq: ctx.streamSeq.current, confirmed: new Set(ctx.confirmedIds.current) };
    try {
      const served = await mp.listCombatants(sessionId);
      // Torn down while the list was on its way (a session change): it speaks for a table this view has left.
      if (stopped) return;
      const docs = served.filter((d) => !goneSince(ctx, asked, d.id));
      healFrom(ctx, docs, why, asked);
      sweepStubs(ctx, mp, sessionId, docs);
      forgetNotesBefore(ctx, asked.seq);
    } catch (e) {
      diag.warn('sync', 'roster audit failed - keeping the view', errorSummary(e));
    } finally {
      auditing = false;
    }
  };
  /** Stop: an audit still on its way when its listener is torn down applies nothing. */
  audit.stop = () => {
    stopped = true;
  };
  return audit;
}
